/**
 * The calendar screen — the home route and the heart of the app.
 *
 * Reads from `EntryContext` and owns only which month is on screen. It reports a
 * chosen day upward rather than opening anything itself, because the day sheet
 * belongs to another feature and features may not import each other
 * (`no-feature-cycles`, architecture Section 4). The composition root wires the
 * two together.
 */
import { useState } from 'react'
import { t } from '@copy/index'
import { addMonths, startOfMonth, type ISODate } from '@domain/index'
import { useEntries, usePrediction } from '@contexts/index'
import { FlowPicker } from '@shared/ui'
import { MonthGrid, monthLabel } from './MonthGrid'

export interface CalendarPageProps {
  onOpenDay: (date: ISODate) => void
}

export function CalendarPage({ onOpenDay }: CalendarPageProps) {
  const { state, entries, today, derived, update } = useEntries()
  const prediction = usePrediction()
  const [month, setMonth] = useState<ISODate>(() => startOfMonth(today))

  const predictedStart = prediction.kind === 'estimate' ? prediction.predictedStart : undefined
  const todayEntry = entries.get(today)

  return (
    <main className="flex h-full flex-col p-4">
      <header className="flex items-center justify-between pb-4">
        <button
          type="button"
          aria-label={t('calendar.previousMonth')}
          className="flex min-h-11 min-w-11 items-center justify-center rounded-lg text-lg transition-all duration-200 hover:bg-line/40 active:scale-90"
          onClick={() => setMonth((m) => addMonths(m, -1))}
        >
          <svg className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
            <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 19.5L8.25 12l7.5-7.5" />
          </svg>
        </button>
        <h1 className="text-base font-medium">{monthLabel(month)}</h1>
        <button
          type="button"
          aria-label={t('calendar.nextMonth')}
          className="flex min-h-11 min-w-11 items-center justify-center rounded-lg text-lg transition-all duration-200 hover:bg-line/40 active:scale-90"
          onClick={() => setMonth((m) => addMonths(m, 1))}
        >
          <svg className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
            <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 4.5l7.5 7.5-7.5 7.5" />
          </svg>
        </button>
      </header>

      {/* The whole entry set lives in memory (EntryContext), so rendering a
          month is a lookup rather than a query. */}
      <MonthGrid
        month={month}
        entries={entries}
        today={today}
        predictedStart={predictedStart}
        onSelectDay={onOpenDay}
      />

      {state === 'ready' && entries.size === 0 ? (
        // FR-308: an empty calendar is a designed screen, not a blank grid.
        // Most users spend their first week here.
        <p className="pt-6 text-center text-sm text-ink-muted">{t('calendar.empty.body')}</p>
      ) : null}

      <section className="mt-auto flex flex-col gap-3 pt-6">
        {/* Tap 2 of the two-tap path (FR-213). Always today, never the browsed
            month — so it cannot silently write to a day the user is only
            looking at. */}
        <FlowPicker
          date={today}
          value={todayEntry?.flow}
          onChange={(flow) => void update(today, { flow })}
        />

        <button
          type="button"
          onClick={() => onOpenDay(today)}
          className="min-h-11 rounded-lg border border-line text-sm transition-all duration-200 hover:bg-line/40 active:scale-[0.98]"
        >
          {t('calendar.moreForToday')}
        </button>

        {derived.currentCycle ? (
          <p className="text-center text-xs text-ink-muted">
            {t('prediction.cycleDay', { day: derived.currentCycle.dayOfCycle })}
          </p>
        ) : null}

        <p className="text-center text-xs text-ink-muted">{t('privacy.localOnly')}</p>
      </section>
    </main>
  )
}
